import { ApiKey, ApiUrl, SessionKey } from "./Constant.js";

export const FAVOURITE_GAMES_CHANGED = "favourite-games-changed";

/** ProductGame id only. Rejects dashboard provider cards (id == masterProductId when hasOwnLobby). */
export function resolveFavouritableGameId(game) {
  if (!game) return null;
  const id = Number(game.gameId ?? game.id);
  if (!(id > 0)) return null;
  if (game.hasOwnLobby === true && Number(game.masterProductId) === id) return null;
  return id;
}

export function storageKeysForMember(memberId) {
  const id = memberId || "anon";
  return {
    data: `${SessionKey._FAVOURITE_GAMES}:${id}`,
    version: `${SessionKey._FAVOURITE_GAMES_VERSION}:${id}`,
  };
}

export function parseGameIds(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(Number).filter((n) => n > 0) : [];
  } catch {
    return [];
  }
}

export function getFavouriteGameIds(memberId) {
  const { data } = storageKeysForMember(memberId);
  return parseGameIds(localStorage.getItem(data));
}

export function getFavouriteVersion(memberId) {
  const { version } = storageKeysForMember(memberId);
  return localStorage.getItem(version) || null;
}

export function writeFavouriteGames(memberId, gameIds, versionId) {
  const { data, version } = storageKeysForMember(memberId);
  localStorage.setItem(data, JSON.stringify(gameIds || []));
  localStorage.setItem(version, String(versionId ?? ""));
  window.dispatchEvent(
    new CustomEvent(FAVOURITE_GAMES_CHANGED, { detail: { memberId, gameIds } })
  );
}

export function clearFavouriteGames(memberId) {
  const { data, version } = storageKeysForMember(memberId);
  localStorage.removeItem(data);
  localStorage.removeItem(version);
  window.dispatchEvent(
    new CustomEvent(FAVOURITE_GAMES_CHANGED, { detail: { memberId, gameIds: [] } })
  );
}

export function isFavouriteGame(memberId, gameId) {
  return getFavouriteGameIds(memberId).includes(Number(gameId));
}

export async function syncFavouriteGamesOnLogin(memberId) {
  if (!memberId) return;
  const { default: ApiEngine } = await import("./ApiEngine.js");
  const responseJson = await ApiEngine.get(ApiUrl._API_GET_FAVOURITE_GAMES);
  if (!responseJson[ApiKey._API_SUCCESS_KEY]) return;
  const payload = responseJson[ApiKey._API_DATA_KEY] || {};
  writeFavouriteGames(memberId, payload.gameIds || [], payload.versionId || null);
}

export async function syncFavouriteGamesOnRefresh(memberId) {
  if (!memberId) return;
  const { version } = storageKeysForMember(memberId);
  if (localStorage.getItem(version) === null) {
    await syncFavouriteGamesOnLogin(memberId);
    return;
  }
  const localVersion = getFavouriteVersion(memberId);
  const { default: ApiEngine } = await import("./ApiEngine.js");
  const responseJson = await ApiEngine.get(ApiUrl._API_GET_FAVOURITE_GAMES_VERSION);
  if (!responseJson[ApiKey._API_SUCCESS_KEY]) return;
  const serverVersion = responseJson[ApiKey._API_DATA_KEY]?.versionId ?? null;
  if (String(serverVersion || "") !== String(localVersion || "")) {
    await syncFavouriteGamesOnLogin(memberId);
  }
}

/**
 * Retrieves the member's favourite ProductGame IDs for one master product.
 * Consumers can pass these IDs to their own paginated game-list query.
 */
export async function getFavouriteGameIdsForMasterProduct(masterProductId) {
  if (masterProductId == null || masterProductId === "") return [];

  const { default: ApiEngine } = await import("./ApiEngine.js");
  const responseJson = await ApiEngine.get(ApiUrl._API_GET_FAVOURITE_GAME_LIST);
  if (!responseJson[ApiKey._API_SUCCESS_KEY]) {
    throw new Error(responseJson[ApiKey._API_MESSAGE_KEY] || "RETRIEVE_FAVOURITE_GAMES_FAILED");
  }

  return (responseJson[ApiKey._API_DATA_KEY] || [])
    .filter(
      (game) =>
        String(game.masterProductId ?? game.MasterProductId) === String(masterProductId)
    )
    .map((game) => game.id ?? game.Id ?? game.gameId ?? game.GameId)
    .filter((id) => id != null && id !== "");
}

export async function toggleFavouriteGame(memberId, gameId) {
  const { default: ApiEngine } = await import("./ApiEngine.js");
  const { createFormBody } = await import("./Util.js");
  const responseJson = await ApiEngine.post(
    ApiUrl._API_TOGGLE_FAVOURITE_GAME,
    createFormBody({ gameId })
  );
  if (!responseJson[ApiKey._API_SUCCESS_KEY]) {
    throw new Error(responseJson[ApiKey._API_MESSAGE_KEY] || "TOGGLE_FAVOURITE_FAILED");
  }
  const payload = responseJson[ApiKey._API_DATA_KEY] || {};
  writeFavouriteGames(memberId, payload.gameIds || [], payload.versionId || null);
  return payload;
}
